import { isAfter, isBefore, subYears } from "date-fns";
import * as z from "zod";

import {
  emailSchema,
  friendlyNameSchema,
  passwordSchema,
  phoneSchema,
  ssnSchema,
  userIdSchema,
  zipCodeSchema,
} from "#app/lib/schemas/field-schemas";
import { transformDatePickerValue } from "#app/lib/schemas/field-validations";
import type { DatePickerProps } from "#ui/date-picker";
import type { StepsItems } from "#ui/steps.components";

// -----------------------------------------------------------------------------
// All steps
// -----------------------------------------------------------------------------

export const steps = [
  { id: "personal-information", children: "Personal Information" },
  { id: "email-verification", children: "Email Verification" },
  { id: "user-id-password", children: "User ID & Password" },
  { id: "terms-of-use", children: "Terms Of Use" },
  // The final "success" step is not included in the steps UI.
] as const satisfies StepsItems;

// IMPORTANT: Keep values in sync with `steps` array indices above
// This isn't strictly necessary, but helps with clarity.
export const stepsStateMapping = {
  step0PersonalInformation: 0,
  step1EmailVerification: 1,
  step2UserIdAndPassword: 2,
  step3TermsOfUse: 3,
  step4Success: 4,
} as const;

export type StepsState =
  (typeof stepsStateMapping)[keyof typeof stepsStateMapping];

// -----------------------------------------------------------------------------
// Step 0: Personal Information
// -----------------------------------------------------------------------------

const AGE_MAX = 125;
const AGE_MIN = 18;

// Calculate date boundaries for age validation
const getMaxDate = () => subYears(new Date(), AGE_MAX);
const getMinDate = () => subYears(new Date(), AGE_MIN);

/**
 * Checks if the date of birth is over 100 years old.
 * Module scoped function to avoid re-renders.
 * Used in schema validation.
 */
function isTooOld(value: string) {
  return isBefore(new Date(value), getMaxDate());
}

/**
 * Checks if the date of birth is under 18 years old.
 * Module scoped function to avoid re-renders.
 * Used in schema validation.
 */
function isTooYoung(value: string) {
  return isAfter(new Date(value), getMinDate());
}

/**
 * Sets the calendar properties for the date picker.
 * Module scoped function to avoid re-renders.
 */
export function getDateOfBirthCalendarProps() {
  const start = getMaxDate();
  const end = getMinDate();
  return {
    startMonth: start,
    endMonth: end,
    disabled: [{ before: start, after: end }],
  } as const satisfies DatePickerProps["calendarProps"];
}

export const RegisterStep0FormSchema = z
  .object({
    firstName: z.string().min(1, {
      error: "First name is required.",
    }),
    lastName: z.string().min(1, {
      error: "Last name is required.",
    }),
    dateOfBirth: z
      .string()
      .min(1, {
        error: "Date of birth is required.",
      })
      .transform(transformDatePickerValue)
      .pipe(
        z.iso.date({
          error: "Date of birth is invalid.",
        }),
      )
      .refine((val) => !isTooOld(val), {
        error: "Date of birth is invalid.",
      })
      .refine((val) => !isTooYoung(val), {
        error: "You must be 18 or older in order to register online.",
      }),
    ssn: ssnSchema,
    email: emailSchema,
    phone: phoneSchema,
    zip: zipCodeSchema,
  })
  .required();

export type RegisterStep0FormValues = z.infer<typeof RegisterStep0FormSchema>;

// -----------------------------------------------------------------------------
// Step 2: User ID & Password
// -----------------------------------------------------------------------------

export const RegisterStep2FormSchema = z
  .object({
    userId: userIdSchema,
    friendlyName: friendlyNameSchema.optional(),
    password: passwordSchema,
    confirmPassword: z.string().min(1, {
      error: "Please confirm your password.",
    }),
  })
  .refine((val) => val.confirmPassword === val.password, {
    error: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export type RegisterStep2FormValues = z.infer<typeof RegisterStep2FormSchema>;

// -----------------------------------------------------------------------------
// Error Messages
// -----------------------------------------------------------------------------

export const ERROR_MESSAGES = {
  // Async errors
  genericError: "Something went wrong, please try again.",
  invalidRegistrationCode: "The registration code is invalid.",
  userIdTaken: (userId: string) => `User ID "${userId}" is already taken.`,
  userCreationFailed: "We are unable to create a user at this time.",
  passwordValidationFailed: (message: string) =>
    `Password validation failed: ${message}`,

  // Form-level generic
  correctErrors: "Please correct all the errors to continue.",
} as const;
